import React from 'react';
import styled from 'styled-components';
import { Editor } from '@companycam/tiptap-editor';
import { FloatingMenu } from '@tiptap/react';

import { ToolbarIconButton } from '../PageToolbar/ToolbarIconButton';
import { Divider } from '../PageToolbar/shared/Divider';
import { translate } from 'javascripts/translations/i18n';

// #region icons and styled-components
const ToolbarWrapper = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
`;

const ButtonWrapper = styled.div`
  display: flex;
  padding: var(--cc_size_spacing_s);
  background-color: var(--cc_color_background_1);
  border-radius: var(--cc_size_border_radius_l);
  box-shadow: 0px var(--cc_size_depth_2_y) 20px var(--cc_color_depth_2);
  border: 1px solid var(--cc_color_border_default);
  align-items: center;
`;
// #endregion

export type TableToolbarProps = {
  editor: Editor;
};

const TableToolbar = ({ editor }: TableToolbarProps) => {
  if (!editor) {
    return null;
  }

  // Editor commands
  const addRowBefore = () => editor.chain().focus().addRowBefore().run();
  const addRowAfter = () => editor.chain().focus().addRowAfter().run();
  const deleteRow = () => editor.chain().focus().deleteRow().run();
  const addColumnBefore = () => editor.chain().focus().addColumnBefore().run();
  const addColumnAfter = () => editor.chain().focus().addColumnAfter().run();
  const deleteColumn = () => editor.chain().focus().deleteColumn().run();
  const deleteTable = () => editor.chain().focus().deleteTable().run();

  const isToolbarVisible = () => {
    return (
      editor.isActive('table') ||
      editor.isActive('tableCell') ||
      editor.isActive('tableHeader') ||
      editor.isActive('tableRow')
    );
  };

  return (
    <FloatingMenu
      editor={editor}
      shouldShow={isToolbarVisible}
      tippyOptions={{
        placement: 'top-start',
        getReferenceClientRect: () =>
          document
            .getElementsByClassName('has-focus')[0]
            .getBoundingClientRect(),
        interactive: true,
        popperOptions: {
          modifiers: [
            {
              name: 'flip',
              options: {
                fallbackPlacements: ['bottom-start'],
              },
            },
          ],
        },
      }}
    >
      <ToolbarWrapper data-testid="page_table-toolbar">
        <ButtonWrapper>
          <ToolbarIconButton
            icon="table-row-plus-before"
            accessibilityLabel={translate('pages.toolbar.addRowBefore')}
            onClick={addRowBefore}
          />
          <ToolbarIconButton
            icon="table-row-plus-after"
            accessibilityLabel={translate('pages.toolbar.addRowAfter')}
            onClick={addRowAfter}
          />
          <ToolbarIconButton
            icon="table-row-remove"
            accessibilityLabel={translate('pages.toolbar.deleteRow')}
            onClick={deleteRow}
          />
          <Divider />
          <ToolbarIconButton
            icon="table-column-plus-before"
            accessibilityLabel={translate('pages.toolbar.addColumnBefore')}
            onClick={addColumnBefore}
          />
          <ToolbarIconButton
            icon="table-column-plus-after"
            accessibilityLabel={translate('pages.toolbar.addColumnAfter')}
            onClick={addColumnAfter}
          />
          <ToolbarIconButton
            icon="table-column-remove"
            accessibilityLabel={translate('pages.toolbar.deleteColumn')}
            onClick={deleteColumn}
          />
          <Divider />
          <ToolbarIconButton
            icon="delete-outline"
            accessibilityLabel={translate('pages.toolbar.deleteTable')}
            onClick={deleteTable}
          />
        </ButtonWrapper>
      </ToolbarWrapper>
    </FloatingMenu>
  );
};

export default TableToolbar;
